import { Caption, Input, Typography, Dropdown, DropdownItem } from 'fronton-react';
import React from 'react';
import { Breakpoint } from 'types/utils';
import { jcm } from 'utils';
import { DurationOfUseType } from 'common/constants';
import { useTranslation } from 'react-i18next';
import {
  formPartHeader,
  shopSelectorDropdown,
  authorDetailInput,
  authorDetailInputAtMobile,
} from '../styles';
import {
  ReviewAuthorDetailShopProps,
  ReviewAuthorDetailTimeUsageProps,
  ReviewAuthorEmailProps,
  ReviewAuthorNameProps,
  ReviewFormFieldError,
} from '../types';
import { FormPart, InputField, LastFormPart, UsageFormPart } from './layout-blocks';
import { ReviewUsageTime } from './review-usage-time';

export const ReviewAuthorDetails: React.FC<
  ReviewAuthorDetailTimeUsageProps &
    ReviewAuthorDetailShopProps &
    ReviewAuthorNameProps &
    ReviewAuthorEmailProps & {
      availabledStores: string[];
      availabledRegions: string[];
      breakpoint: Breakpoint;
      errors: ReviewFormFieldError[] | null;
    }
> = ({
  breakpoint,
  timeUsage,
  region,
  shop,
  availabledStores,
  availabledRegions,
  authorName,
  authorEmail,
  errors,
  setShop,
  setTimeUsage,
  setAuthorName,
  setAuthorEmail,
  onAuthorNameFieldFocus,
  onAuthorEmailFieldFocus,
}) => {
  const { t } = useTranslation();
  const doSetTimeUsage = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setTimeUsage(
        event.target.value as DurationOfUseType,
        Number(event.target.getAttribute('data-ga-index')),
      );
    },
    [setTimeUsage],
  );
  const doSetShop = React.useCallback(
    (selectedShop: string | string[] | null) => {
      if (typeof selectedShop === 'string') setShop(region, selectedShop);
    },
    [setShop, region],
  );
  const doSetRegion = React.useCallback(
    (selectedRegion: string | string[] | null) => {
      if (typeof selectedRegion === 'string') setShop(selectedRegion, null);
    },
    [setShop],
  );
  const [showAuthorEmailError, setShowAuthorEmailError] = React.useState<boolean>(true);
  const doSetAuthorName = React.useCallback(
    (_: React.SyntheticEvent, value: string) => setAuthorName(value),
    [setAuthorName],
  );
  const doSetAuthorEmail = React.useCallback(
    (_: React.SyntheticEvent, value: string) => {
      setShowAuthorEmailError(false);
      setAuthorEmail(value);
    },
    [setAuthorEmail],
  );
  const onAuthorNameFieldFocusCb = React.useCallback(
    () => onAuthorNameFieldFocus(),
    [onAuthorNameFieldFocus],
  );
  const onAuthorEmailFieldFocusCb = React.useCallback(
    () => onAuthorEmailFieldFocus(),
    [onAuthorEmailFieldFocus],
  );

  const atMobile = breakpoint === 'mobile';
  const timeUsageSafe: string | null = timeUsage?.value ?? null;
  const authorNameErrorCaption = errors?.filter((r) => r.fieldKey === 'author_name')[0]?.message ?? null;
  const authorEmailErrorCaption = errors?.filter((r) => r.fieldKey === 'author_email')[0]?.message ?? null;
  const canSelectShop = availabledRegions.length > 0;
  const disableShopDropdown = region === null || region.length === 0;
  return (
    <>
      <UsageFormPart data-qa="time-usage-wrapper">
        <Typography variant="m" size="subtitle" as="h3" className={formPartHeader}>
          {t('reviews.form.term.title')}
        </Typography>
        <ReviewUsageTime value={timeUsageSafe} setTime={doSetTimeUsage} />
      </UsageFormPart>
      {canSelectShop && (
        <FormPart>
          <Typography variant="m" size="subtitle" as="h3" className={formPartHeader}>
            {t('reviews.form.place.title')}
          </Typography>
          {/* data-qa is not working on `Dropdown` component */}
          <div data-qa="review-form-city-dropdown_mf-pdp">
            <Dropdown
              className={jcm({
                [shopSelectorDropdown]: true,
                [authorDetailInputAtMobile]: atMobile,
              })}
              mobile={atMobile ? 'full-screen' : undefined}
              size="m"
              label={t('reviews.form.place.city')}
              placeholder={t('reviews.form.place.input')}
              value={region ?? undefined}
              onSelect={doSetRegion}
            >
              {availabledRegions.map((value) => (
                <DropdownItem value={value} text={value} key={value} />
              ))}
            </Dropdown>
          </div>
          <div data-qa="review-form-shop-dropdown_mf-pdp">
            <Dropdown
              disabled={disableShopDropdown}
              className={jcm({
                [shopSelectorDropdown]: true,
                [authorDetailInputAtMobile]: atMobile,
              })}
              mobile={atMobile ? 'full-screen' : undefined}
              size="m"
              label={t('reviews.form.place.shop')}
              placeholder={t('reviews.form.place.input')}
              value={shop ?? undefined}
              onSelect={doSetShop}
            >
              {availabledStores.map((value) => (
                <DropdownItem text={value} value={value} key={value} />
              ))}
            </Dropdown>
          </div>
        </FormPart>
      )}
      <LastFormPart data-qa="author-details-contacts-wrapper">
        <Typography variant="m" size="subtitle" as="h3" className={formPartHeader}>
          {t('reviews.form.contacts.title')}
        </Typography>
        <InputField
          input={
            <Input
              className={jcm({
                [authorDetailInput]: true,
                [authorDetailInputAtMobile]: atMobile,
              })}
              mobile={atMobile || undefined}
              data-qa="review-form-author-name-input_mf-pdp"
              inputSize="m"
              label={t('reviews.form.contacts.name')}
              placeholder={t('reviews.form.contacts.name')}
              value={authorName ?? undefined}
              onChange={doSetAuthorName}
              onFocus={onAuthorNameFieldFocusCb}
              error={!!authorNameErrorCaption}
              caption={<Caption error message={authorNameErrorCaption} />}
            />
          }
        />
        <InputField
          input={
            <Input
              className={jcm({
                [authorDetailInput]: true,
                [authorDetailInputAtMobile]: atMobile,
              })}
              mobile={atMobile || undefined}
              data-qa="review-form-author-email-input_mf-pdp"
              inputSize="m"
              label={t('reviews.form.contacts.email')}
              placeholder={t('reviews.form.contacts.email')}
              value={authorEmail ?? undefined}
              onChange={doSetAuthorEmail}
              onFocus={onAuthorEmailFieldFocusCb}
              onBlur={() => setShowAuthorEmailError(true)}
              error={!!(showAuthorEmailError && authorEmailErrorCaption)}
              caption={<Caption error message={authorEmailErrorCaption} />}
            />
          }
        />
      </LastFormPart>
    </>
  );
};
